import {
  Box,
  ConfirmationDialogBox,
  Grid,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  DeleteIcon,
  InformationOutlineIcon,
  PlusCircleIcon,
  SyncIcon,
} from '@atgfr8/icons';
import { BasicModal, BasicTable, Button } from '@components';
import { Column } from '@components/BasicTable/BasicTable';
import { FeatureFlags } from '@constants/featureFlags';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  deleteFalveyInsurance,
  fetchQuoteById,
  fetchQuoteParametersByQuoteId,
} from '@store/ltl/services';
import { ltlQuoteState, setLTLQuote } from '@store/ltl/store';
import { FalveyInsuranceQuoteInLoad } from '@store/ltl/types';
import { closeModal, openModal } from '@store/modals';
import {
  createLtlQuoteLegacy,
  FalveyInsuranceQuote,
  fetchLtlQuoteAccessorials,
  fetchLtlQuoteByLoadId,
  fetchLtlQuoteInsurance,
  fetchLtlQuoteLoadItems,
  fetchLtlQuoteParametersByLoadId,
  LtlQuoteAccessorialViewModel,
  LtlQuoteLoadItemViewModel,
  LtlQuoteParametersViewModel,
  LtlQuoteViewModel,
  mapLoadItems,
  orderStore,
  updateLoadPropertyAtIndex,
} from '@store/orders';
import { applyOrderHydration } from '@store/orders/actions/orderFormActions';
import {
  isFalveyInsuranceCommitted,
  updateCustomerAndVendorPay,
} from '@store/orders/helper';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { ConfigManager } from '@utils/ConfigManager';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { formatDateString } from '@utils/dateFormat';
import { formatAmount, isAdmin } from '@utils/utils';
import { InsuranceUpdateAndConfirmationForm } from '@views/ltl/components/InsuranceUpdateAndConfirmation';
import { isAllowInsuranceDeletion } from '@views/ltl/useLtlComponents';
import { batch, createEffect, createSignal, onCleanup, Show } from 'solid-js';

import downArrow from '../../../../../assets/downArrow.svg';
import upArrow from '../../../../../assets/upArrow.svg';
import { LtlQuotingModal } from '../../ltlQuotingModal';
import { classes } from './classes';
import {
  getAccessorialString,
  isLoadDispatched,
  LTLHeaderText,
} from './helpers';
import { ApiRequestModal } from './LtlApiRequestSummary';
import './style.css';
import { TerminalInfoModal } from './TerminalInfoModal';

interface LTLQuoteProps {
  status?: string;
  loadId: number;
  load: ILoadEditViewModel;
  quoteId: string;
  legacyQuoteId: string | undefined;
  insuranceDeleteChangeTrackingSource?: ChangeTrackingSource;
}

const ADD_INSURANCE_EXCLUDED_STATUSES = ['Empty', 'Billed'];

const LTLQuote = (props: LTLQuoteProps) => {
  const isInsuranceDeleteEnabled = isAllowInsuranceDeletion();
  const showCarrierLiabilityInfo = isFeatureFlagEnabled(
    FeatureFlags.LtlLiabilityEducation,
  )();
  const columns: Column<LtlQuoteLoadItemViewModel>[] = [
    {
      name: 'Quantity',
      key: 'quantity',
      renderCell: (rowData) =>
        rowData.parentQuoteLoadItemId != undefined ? (
          <span class="no-bottom-border"></span>
        ) : (
          <span>{String(rowData.quantity)}</span>
        ),
    },
    {
      name: 'Weight',
      key: 'weight',
      renderCell: (rowData) => <span>{String(rowData.weight)}</span>,
    },
    {
      name: 'Class',
      key: 'class',
      renderCell: (rowData) => <span>{String(rowData.class)}</span>,
    },
    {
      name: 'NMFC',
      key: 'nmfc',
      renderCell: (rowData) => <span>{String(rowData.nmfc)}</span>,
    },
    {
      name: 'Dimension',
      key: 'dimensions',
      renderCell: (rowData) =>
        rowData.parentQuoteLoadItemId != undefined ? (
          <span></span>
        ) : (
          <span>{String(rowData.dimensions)}</span>
        ),
    },
    {
      name: 'Hazmat',
      key: 'hazmat',
      renderCell: (rowData) => <span>{String(rowData.hazmat)}</span>,
    },
  ];

  const [ltlQuoteParams, setLtlQuoteParams] =
    createSignal<LtlQuoteParametersViewModel>({
      id: 0,
      value: false,
      operationType: 'None',
      quoteDate: '',
      originZip: '',
      originCity: '',
      originState: '',
      originCountry: '',
      destinationZip: '',
      destinationCity: '',
      destinationState: '',
      destinationCountry: '',
      linearFeet: 0,
      isStackable: false,
      isLinearFeetOverridden: false,
      createdByName: '',
      provider: '',
      accessorials: [],
      ltlInsuranceQuote: null,

      quoteRequestLocator: '',
      willExpireSoon: false,
    });

  const [ltlQuote, setLtlQuote] = createSignal<LtlQuoteViewModel>({
    id: 0,
    operationType: 'None',
    quoteDate: '',
    carrierTotal: 0,
    ltlInsurancePolicy: null,
  });

  const [loadItems, setLoadItems] = createSignal<LtlQuoteLoadItemViewModel[]>(
    [],
  );
  const [accessorials, setAccessorials] = createSignal<
    LtlQuoteAccessorialViewModel[]
  >([]);
  const [insuranceQuote, setInsuranceQuote] =
    createSignal<FalveyInsuranceQuote>();

  createEffect(async () => {
    const [quoteParams, quote] =
      props.quoteId !== ''
        ? await Promise.all([
            fetchQuoteParametersByQuoteId(Number(props.quoteId)),
            fetchQuoteById(Number(props.quoteId)),
          ])
        : await Promise.all([
            fetchLtlQuoteParametersByLoadId(props.loadId),
            fetchLtlQuoteByLoadId(props.loadId),
          ]);

    if (quote !== null && quote.id !== 0) {
      batch(() => {
        setLtlQuoteParams(quoteParams);
        applyOrderHydration(() => {
          updateLoadPropertyAtIndex({
            ltlQuoteParameters: {
              ...quoteParams,
            },
          });
        });
        setLtlQuote(quote);
      });
      await fetchAdditionalQuoteDetails(quoteParams.id);
      setLTLQuote({
        ltlQuote: createLtlQuoteLegacy(ltlQuoteParams(), ltlQuote()),
        originZip: ltlQuoteParams().originZip,
        originCity: ltlQuoteParams().originCity,
        originState: ltlQuoteParams().originState,
        originCountry: ltlQuoteParams().originCountry,
        destinationZip: ltlQuoteParams().destinationZip,
        destinationCity: ltlQuoteParams().destinationCity,
        destinationState: ltlQuoteParams().destinationState,
        destinationCountry: ltlQuoteParams().destinationCountry,
        customerId: ltlQuoteParams().customerId ?? undefined,
        customerName: ltlQuoteParams().customerName ?? undefined,
        pickupDate: ltlQuoteParams().pickupDate,
        loadItems: mapLoadItems(loadItems()),
        originAddressMismatch: ltlQuoteState.quote?.originAddressMismatch,
        destinationAddressMismatch:
          ltlQuoteState.quote?.destinationAddressMismatch,
      });
    }
  });

  onCleanup(() => {
    setLtlQuoteParams({
      id: 0,
      value: false,
      operationType: 'None',
      quoteDate: '',
      originZip: '',
      originCity: '',
      originState: '',
      originCountry: '',
      destinationZip: '',
      destinationCity: '',
      destinationState: '',
      destinationCountry: '',
      linearFeet: 0,
      isStackable: false,
      isLinearFeetOverridden: false,
      createdByName: '',
      provider: '',
      accessorials: [],
      ltlInsuranceQuote: null,

      quoteRequestLocator: '',
      willExpireSoon: false,
    });
  });

  const fetchAdditionalQuoteDetails = async (quoteParamsId: number) => {
    if (quoteParamsId === 0) return;
    const [loadItems, accessorials, insurance] = await Promise.all([
      fetchLtlQuoteLoadItems(quoteParamsId),
      fetchLtlQuoteAccessorials(quoteParamsId),
      fetchLtlQuoteInsurance(quoteParamsId),
    ]);

    batch(() => {
      setLoadItems(loadItems);
      setAccessorials(accessorials);
      setInsuranceQuote(insurance);
      setLtlQuoteParams({
        ...ltlQuoteParams(),
        accessorials: accessorials,
        ltlInsuranceQuote: insurance,
        loadItems: loadItems,
      });
    });
  };

  const formatCurrency = (value: number | undefined) => {
    if (typeof value === 'number' && isFinite(value) && value > 0) {
      return `$${value.toFixed(2)}`;
    }
    return '$ --.--';
  };

  const getVendorInsurance = () => {
    return getInsurancePricingFromLoad()?.premium ?? 0;
  };

  const getAgentPriceWithInsurance = () => {
    let totalAgentCost = 0;
    if (insuranceQuote()?.id !== 0) {
      totalAgentCost += insuranceQuote()?.premium ?? 0;
    } else {
      totalAgentCost += getVendorInsurance();
    }

    return formatAmount(totalAgentCost + ltlQuote().carrierTotal);
  };

  const getCustomerInsurance = () => {
    const insurancePricing = getInsurancePricingFromLoad();
    return (
      (insurancePricing?.premium ?? 0) + (insurancePricing?.serviceFee ?? 0)
    );
  };

  const getCustomerPriceWithInsurace = () => {
    let totalCustomerCost = 0;
    if (insuranceQuote()?.id !== 0) {
      totalCustomerCost =
        (insuranceQuote()?.premium ?? 0) + (insuranceQuote()?.serviceFee ?? 0);
    } else {
      totalCustomerCost = getCustomerInsurance();
    }
    return formatAmount(
      totalCustomerCost + (ltlQuote().customerTotal as number),
    );
  };

  const getInsuranceRate = () => {
    return insuranceQuote()?.id !== 0
      ? formatAmount(insuranceQuote()?.premium)
      : '--';
  };

  const getInsuranceAndFees = () => {
    if (insuranceQuote()?.id !== 0) {
      return formatAmount(
        (insuranceQuote()?.premium ?? 0) + (insuranceQuote()?.serviceFee ?? 0),
      );
    }
  };

  const isAllowedToDeleteFalveyInsurance = () => {
    return (
      isAdmin() &&
      isFalveyInsuranceCommitted() &&
      isLoadDispatched(props.status ?? '') &&
      isInsuranceDeleteEnabled
    );
  };

  const isAdminAllowedToAddInsurance = () => {
    const hasInsurance = Boolean(insuranceQuote()?.shippingSumInsured);
    return (
      isAdmin() &&
      !hasInsurance &&
      !ADD_INSURANCE_EXCLUDED_STATUSES.includes(props.status ?? '')
    );
  };
  const canOpenInsuranceModal = () => {
    const declaredValue = insuranceQuote()?.shippingSumInsured;
    const isAssigned = ['Assigned', 'Unassigned', 'Can Get'].includes(
      props.status as string,
    );
    if (isAssigned) {
      return !declaredValue || !isFalveyInsuranceCommitted();
    }
    if (!declaredValue) {
      return isAdminAllowedToAddInsurance();
    }
    return false;
  };
  const renderFalveyInsuranceSection = () => {
    const declaredValue = insuranceQuote()?.shippingSumInsured;
    const isAssigned = ['Assigned', 'Unassigned', 'Can Get'].includes(
      props.status as string,
    );
    const isDeclaredValuePresent = Boolean(declaredValue);
    const amountJsx = (
      <div>
        <Typography variant="body1" component={'span'} class="mr-2!">
          {formatAmount(declaredValue)}
        </Typography>
        <Show when={isAllowedToDeleteFalveyInsurance()}>
          <Button
            label="Delete"
            variant="contained"
            startIcon={<DeleteIcon size={20} />}
            class="bg-[#026EA1]!"
            onClick={() => {
              openDialogBox(`deleteInsurance-${ltlQuote().id}`);
            }}
            dataTestId={formatDataTestId('Delete', 'button')}
          />

          <ConfirmationDialogBox
            isDialogOpen={checkIfDialogBoxIsOpen(
              `deleteInsurance-${ltlQuote().id}`,
            )}
            closeDialogBox={(_reason) =>
              closeDialogBox(`deleteInsurance-${ltlQuote().id}`)
            }
            dataTestId={`deleteInsurance-${ltlQuote().id}`}
            id={`deleteInsurance-${ltlQuote().id}`}
            title={
              'Are you sure you want to delete Falvey insurance from this load? This will delete the line items, COI, carrier charges, and vendor tab. Please reach out to Falvey to ensure the insurance is not billed'
            }
            onSubmit={async () => {
              const success = await deleteFalveyInsurance(
                props.loadId,
                props.insuranceDeleteChangeTrackingSource,
              );
              if (success) {
                window.location.reload();
              }
            }}
            onSubmitText="Ok"
          />
        </Show>
      </div>
    );

    const buttonsJsx = (
      <Show
        when={declaredValue}
        fallback={
          <Button
            label="Add Insurance"
            variant="contained"
            startIcon={<PlusCircleIcon size={20} />}
            class="bg-[#026EA1]!"
            onClick={() => openModal('falvey-insurance')}
            dataTestId={formatDataTestId('Add Insurance', 'button')}
          />
        }
      >
        {amountJsx}
        <Show when={!isFalveyInsuranceCommitted()}>
          <Button
            label="Update"
            variant="contained"
            startIcon={<SyncIcon size={20} />}
            class="bg-[#026EA1]!"
            onClick={() => openModal('falvey-insurance')}
            dataTestId={formatDataTestId('Update', 'button')}
          />
        </Show>
      </Show>
    );
    if (isAssigned) {
      return [LTLHeaderText('Declared Value'), buttonsJsx];
    }

    if (isDeclaredValuePresent) {
      return [LTLHeaderText('Declared Value'), amountJsx];
    }

    if (isAdminAllowedToAddInsurance()) {
      return [LTLHeaderText('Declared Value'), buttonsJsx];
    }

    return [];
  };

  const loadItemsAndBreakdowns = () => {
    const items = loadItems();
    const result: LtlQuoteLoadItemViewModel[] = [];

    for (const item of items) {
      const breakdowns = item.quoteLoadItemBreakdown;

      if (
        breakdowns !== undefined &&
        breakdowns !== null &&
        breakdowns.length > 0
      ) {
        item._hasBreakdown = true;
      }

      result.push(item);

      if (
        breakdowns !== undefined &&
        breakdowns !== null &&
        breakdowns.length > 0
      ) {
        for (const child of breakdowns) {
          child._isBreakdown = true;
          result.push(child);
        }
      }
    }

    return result;
  };

  // To get insurance pricing from load after dispatch
  const getInsurancePricingFromLoad = () => {
    return insuranceQuote()?.id !== 0 ? insuranceQuote() : undefined;
  };

  return (
    <Show when={ltlQuote().id !== 0}>
      <div class="mt-2">
        <div class="flex items-center space-x-2">
          <div class="text-[#123b50] text-xl font-medium">
            LTL Quote - {ltlQuote().carrierQuoteId}
          </div>
          <div class="text-[#666666] text-sm flex space-x-1">
            <div class="font-medium"> Created By:</div>
            <div>{ltlQuoteParams().createdByName}</div>
          </div>
          <div class="text-[#666666] text-sm flex space-x-1">
            <div class="font-medium"> Date Created:</div>
            <div>{formatDateString(ltlQuote().quoteDate, 'MM/dd/yyyy')}</div>
          </div>
        </div>
        <Grid container>
          <Grid item size={3}>
            {LTLHeaderText(ltlQuote().carrierName as string)}
            <img
              class="max-w-40"
              src={`${ConfigManager.apiUrl}/api/carrier/logo/${
                ltlQuote().carrierId
              }`}
              alt={ltlQuote().carrierName}
            />
          </Grid>
          <Grid item size={3}>
            {LTLHeaderText('Accessorials')}
            <Stack class={classes.AccessorialsRow}>
              <Grid container>
                <Grid item size={1.5}>
                  <img src={upArrow} alt="Up Arrow" />
                </Grid>
                <Grid item size={10.5}>
                  <Typography variant="body1" class="text-xs!">
                    {getAccessorialString(
                      accessorials()
                        .filter(
                          (acc) => acc.accessorialDescription === 'Pickup',
                        )
                        .map((acc) => acc.accessorialName),
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container>
                <Grid item size={1.5}>
                  <img src={downArrow} alt="Down Arrow" />
                </Grid>{' '}
                <Grid item size={10.5}>
                  <Typography variant="body1" class="text-xs!">
                    {getAccessorialString(
                      accessorials()
                        .filter(
                          (acc) => acc.accessorialDescription === 'Delivery',
                        )
                        .map((acc) => acc.accessorialName),
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="body1" class="text-xs!">
                {getAccessorialString(
                  accessorials()
                    .filter(
                      (acc) =>
                        acc.accessorialDescription !== 'Pickup' &&
                        acc.accessorialDescription !== 'Delivery',
                    )
                    .map((acc) => acc.accessorialName),
                )}
              </Typography>
            </Stack>
          </Grid>
          <Grid item size={3}>
            {showCarrierLiabilityInfo && canOpenInsuranceModal() ? (
              <button
                type="button"
                class="inline-flex items-center justify-center rounded p-0.5 focus:outline-none focus:ring-2 focus:ring-[#9ECBE3] cursor-pointer transition-colors hover:bg-[#E8F3FA] gap-1"
                onClick={() => openModal('falvey-insurance')}
              >
                <InformationOutlineIcon size={20} color="#026ea1" />
                {LTLHeaderText('Max Carrier Liabillity')}
              </button>
            ) : (
              LTLHeaderText('Max Carrier Liabillity')
            )}
            <div class="my-[5px] text-[#666666]">
              <span class="text-[#000000DE] font-bold text-xs"> New: </span>
              {formatCurrency(ltlQuote().maxNewLiability)}
            </div>
            <div class="my-[5px] text-[#666666] ">
              <span class="text-[#000000DE] font-bold text-xs"> Used: </span>
              {formatCurrency(ltlQuote().maxUsedLiability)}
            </div>
            <div class="flex space-x-1 items-center">
              <InformationOutlineIcon size={20} color="#026ea1" />
              <div
                class="text-[#026ea1] font-medium cursor-pointer text-xs"
                onClick={() => {
                  openModal('terminal-info-modal');
                }}
              >
                View Terminal Info
              </div>
            </div>
          </Grid>
          <Grid item size={3}>
            {LTLHeaderText('Cost')}
            <div class="flex">
              <Stack>
                <div class="font-semibold text-sm">Agent</div>
                <div class="text-[#666666] text-xs ml-4">Carrier Rate</div>
                <Show when={insuranceQuote()?.shippingSumInsured !== 0}>
                  <div class="text-[#666666] text-xs ml-4">Insurance Rate</div>
                </Show>
              </Stack>
              <Stack class="ml-auto!">
                <span class="text-[#248670] font-medium">
                  {getAgentPriceWithInsurance()}
                </span>
                <span class="text-xs ml-auto text-[#666666]">
                  {formatAmount(ltlQuote().carrierTotal)}
                </span>
                <Show when={insuranceQuote()?.shippingSumInsured !== 0}>
                  <span class="text-xs ml-auto text-[#666666]">
                    {getInsuranceRate()}
                  </span>
                </Show>
              </Stack>
            </div>
            {Boolean(ltlQuote().customerTotal) && (
              <Box class="flex mt-2">
                <Stack>
                  <div class="font-semibold text-sm">Customer</div>
                  <Show when={insuranceQuote()?.shippingSumInsured !== 0}>
                    <div class="text-[#666666] text-sm ml-4">
                      Insurance & Fees
                    </div>
                  </Show>
                </Stack>
                <Stack class="ml-auto!">
                  <span class="text-[#26856f] font-medium">
                    {getCustomerPriceWithInsurace()}
                  </span>
                  <Show when={insuranceQuote()?.shippingSumInsured !== 0}>
                    <span class="text-sm ml-auto text-[#666666]">
                      {getInsuranceAndFees()}
                    </span>
                  </Show>
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
        <Grid container>
          <Grid item size={3}>
            {LTLHeaderText('Est. Transit Days')}
            <Typography variant="body1">{ltlQuote().transitDays}</Typography>
          </Grid>
          <Grid item size={3}>
            {LTLHeaderText('Service Type')}
            <Typography variant="body1">
              {ltlQuote().serviceTypeDisplayName}
            </Typography>
          </Grid>
          <Grid item size={3}>
            {LTLHeaderText('Movement Type')}
            <Typography variant="body1">
              {ltlQuote().movementType ?? '--'}
            </Typography>
          </Grid>
          <Grid item size={3}>
            {renderFalveyInsuranceSection()}
          </Grid>
        </Grid>
        <div class="pt-5">
          <BasicTable<LtlQuoteLoadItemViewModel>
            noEmptyRender
            columns={columns}
            rows={loadItemsAndBreakdowns()}
            getRowClass={(row, i, allRows) => {
              if (
                row.parentQuoteLoadItemId == null &&
                !(row._hasBreakdown ?? false)
              ) {
                return '';
              } else if (row._hasBreakdown ?? false) {
                return 'no-bottom-border';
              }

              const isLastBreakdown =
                i + 1 >= allRows.length ||
                allRows[i + 1].parentQuoteLoadItemId !==
                  (row.parentQuoteLoadItemId ?? -1);

              return isLastBreakdown ? '' : 'no-bottom-border';
            }}
          />
        </div>
        <div class="flex justify-end space-x-2 my-2">
          {Boolean(isAdmin()) && (
            <Button
              variant="outlined"
              label="View API Call History"
              onClick={() => {
                openModal('api-request-modal');
              }}
              dataTestId={formatDataTestId('View API Call History', 'button')}
            />
          )}
          <ApiRequestModal
            modalId="api-request-modal"
            onClose={closeModal}
            quoteId={
              props.quoteId && props.quoteId.trim() !== ''
                ? props.quoteId
                : String(ltlQuote().id)
            }
          />
        </div>
      </div>
      <BasicModal
        id={'falvey-insurance'}
        width={'650px'}
        footer={false}
        header={false}
      >
        <InsuranceUpdateAndConfirmationForm
          items={loadItems().map((ltlQuoteItem, ind) => {
            let loadItem; //TODO: investigate if this is correct, doing this order store stuff
            if (orderStore.order.loads[orderStore.activeTab.index].items) {
              loadItem =
                orderStore.order.loads[orderStore.activeTab.index].items![ind];
            }
            return {
              ...ltlQuoteItem,
              description: loadItem?.description,
            };
          })}
          onConfirmation={async (val, type, declaredVal) => {
            updateCustomerAndVendorPay(
              val as FalveyInsuranceQuoteInLoad,
              type as string,
              declaredVal as number,
            );
            setInsuranceQuote({
              ...insuranceQuote(),
              shippingSumInsured: val?.shippingSumInsured,
              premium: val?.quotePremium,
              serviceFee: val?.quoteServiceFee,
            } as FalveyInsuranceQuote);
            closeModal('falvey-insurance');
            const updatedInsurance = await fetchLtlQuoteInsurance(
              ltlQuoteParams().id,
            );
            batch(() => {
              setInsuranceQuote(updatedInsurance);
              setLtlQuoteParams({
                ...ltlQuoteParams(),
                ltlInsuranceQuote: updatedInsurance,
              });
              updateLoadPropertyAtIndex({
                ltlQuoteParameters: {
                  ...ltlQuoteParams(),
                  ltlInsuranceQuote: updatedInsurance,
                },
              });
            });
          }}
          onCancel={() => {
            closeModal('falvey-insurance');
          }}
          declaredValue={insuranceQuote()?.shippingSumInsured}
          customerCost={ltlQuote().customerTotal}
          carrierCost={ltlQuote().carrierTotal}
          orderId={orderStore.order.id > 0 ? orderStore.order.id : undefined}
          updateDeclaredValue={(val) => {
            if (insuranceQuote()?.id !== 0) {
              updateLoadPropertyAtIndex({
                ltlQuoteParameters: {
                  ...ltlQuoteParams(),
                  ltlInsuranceQuote: {
                    ...insuranceQuote(),
                    shippingSumInsured: val,
                    premium: insuranceQuote()?.premium ?? 0,
                    serviceFee: insuranceQuote()?.serviceFee ?? 0,
                    createdBy: insuranceQuote()?.createdBy ?? 0,
                    createdDate: insuranceQuote()?.createdDate ?? '',
                    declaredValue: insuranceQuote()?.declaredValue ?? 0,
                    id: insuranceQuote()?.id ?? 0,
                    modifiedBy: insuranceQuote()?.modifiedBy ?? null,
                    modifiedDate: insuranceQuote()?.modifiedDate ?? null,
                    quoteParametersId: ltlQuoteParams().id,
                  },
                },
              });
            }
          }}
          falveyInsuranceQuote={{
            shippingSumInsured: insuranceQuote()?.shippingSumInsured ?? 0,
            quotePremium: insuranceQuote()?.premium ?? 0,
            quoteServiceFee: insuranceQuote()?.serviceFee ?? 0,
          }}
          ltlQuoteId={props.legacyQuoteId}
          quoteParametersId={ltlQuoteParams().id}
        />
      </BasicModal>
      <TerminalInfoModal
        modalId={'terminal-info-modal'}
        onClose={closeModal}
        quoteId={
          props.quoteId && props.quoteId.trim() !== ''
            ? props.quoteId
            : String(ltlQuote().id)
        }
      />
      <LtlQuotingModal loadId={props.loadId} />
    </Show>
  );
};

export default LTLQuote;
